import { format } from 'date-fns';
import { useAppSelector } from '../../app/hooks';
import { selectDate } from '../../app/calendarSlice';
import { useEffect, useState } from 'react';
import {
    MenuItemCategory,
    MenuItemGroup
} from '../../api-types/models/MenuItemDto';
import { useTranslation } from 'react-i18next';
import { FilterSelect } from '../../components/filterSelect';
import { Search } from '../../components/search';
import { useGetCategoriesQuery, useGetGroupsQuery } from '../../api/menuApi';
import { selectConfig } from '../../app/configSlice';
import { DateSelector } from '../../components/dateSelector';
import { useGetAllOrdersForDateQuery } from '../../api/ordersApi';
import { StatisticsTable } from '../../components/statisticsTable';
import { joinOrdersStatistics } from '../../utils/joinOrdersStatistics';

import './index.css';
import { CustomEvent } from '@piwikpro/react-piwik-pro';

export function StatisticsPage() {
    const selectedDate = useAppSelector(selectDate);
    const config = useAppSelector(selectConfig);
    const { t } = useTranslation();

    const dateIso = format(selectedDate, 'yyyy-MM-dd');
    const { data: statisticsForToday = [] } =
        useGetAllOrdersForDateQuery(dateIso);

    const [allGroups, setAllGroups] = useState<string[]>([]);
    const [allCategories, setAllCategories] = useState<string[]>([]);
    const [activeGroup, setActiveGroup] = useState<MenuItemGroup | null>(null);
    const [activeCategory, setActiveCategory] =
        useState<MenuItemCategory | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const { data: groupsData } = useGetGroupsQuery(undefined, {
        skip: !config
    });
    const { data: categoriesData } = useGetCategoriesQuery(undefined, {
        skip: !config
    });

    useEffect(() => {
        if (groupsData) {
            setAllGroups(groupsData.map((group) => group.name));
        }
    }, [groupsData]);

    useEffect(() => {
        if (categoriesData) {
            setAllCategories(categoriesData.map((category) => category.name));
        }
    }, [categoriesData]);

    useEffect(() => {
        CustomEvent.trackEvent('page_view', 'Statistics');
    }, []);

    const filteredStatistics = statisticsForToday.filter(
        (item) =>
            (activeGroup ? item.menuItem.group === activeGroup : true) &&
            (activeCategory
                ? item.menuItem.category === activeCategory
                : true) &&
            item.menuItem.title.toLowerCase().includes(searchTerm)
    );

    // TODO: remove joining statistics once BE will implement proper calculations
    const statisticsArray = joinOrdersStatistics(filteredStatistics);

    return (
        <div className='statistics-page'>
            <div className='content'>
                <div className='filters'>
                    <div className='filters-group'>
                        <FilterSelect<MenuItemGroup>
                            label={t('groups')}
                            options={[
                                { label: t('all_groups') ?? '', value: null },
                                ...allGroups
                                    .filter(
                                        (group) =>
                                            group !== MenuItemGroup.USUAL_ITEMS
                                    )
                                    .map((type: string) => ({
                                        label: type,
                                        value: type
                                    }))
                            ]}
                            handleOnChange={setActiveGroup}
                            placeholder={t('all_groups')}
                        />
                        <FilterSelect
                            options={[
                                {
                                    label: t('all_categories') ?? '',
                                    value: null
                                },
                                ...allCategories.map((type: string) => ({
                                    label: type,
                                    value: type
                                }))
                            ]}
                            handleOnChange={setActiveCategory}
                            placeholder={t('all_categories')}
                            label={t('categories')}
                        />
                        <Search setSearch={setSearchTerm} />
                    </div>
                    <DateSelector />
                </div>
                <StatisticsTable statisticsArray={statisticsArray} />
            </div>
        </div>
    );
}
